import { Pill } from './Pill';

/**
 * StepPills — VERBATIM port from ~/FrontendFinal/Design/src/components/StepPills.tsx.
 *
 * The wizard progress row. `WIZARD_STEPS` is the design's four-step copy, and
 * `activeStep` is 1-based (the numbers the design draws). The active step moves
 * from the `subtle` (0.1) tone to `strong` (0.6) AND changes its label colour;
 * see the Design repo's header for the SVG provenance of both axes.
 *
 * NOTE the backend has THREE stages (details/instructions/review) but the design
 * draws FOUR steps — the fourth, "Publish your tool", is the post-upload done
 * screen. The reskin drives `activeStep` from `vm.stageIndex + 1` so the pills
 * track the live wizard stage.
 */

/** Read off P2–P5 in order. The copy is the design's, verbatim. */
export const WIZARD_STEPS = [
  'Describe your tool',
  'Detail your instructions',
  'Review your writeup',
  'Publish your tool',
] as const;

export interface StepPillsProps {
  /** 1-based, matching the numbers the design draws. */
  activeStep: number;
  className?: string;
}

export function StepPills({ activeStep, className = '' }: StepPillsProps) {
  return (
    <ol
      className={[
        'flex min-w-0 flex-nowrap items-center gap-step-gap overflow-clip',
        className,
      ].join(' ')}
      aria-label="Upload progress"
    >
      {WIZARD_STEPS.map((label, i) => {
        const step = i + 1;
        const active = step === activeStep;

        return (
          <li key={label} className="shrink-0">
            <Pill
              tone={active ? 'strong' : 'subtle'}
              aria-current={active ? 'step' : undefined}
              className={['gap-step-number', active ? '' : 'text-text-inactive'].join(' ')}
            >
              <span aria-hidden="true">{step}</span>
              <span>{label}</span>
            </Pill>
          </li>
        );
      })}
    </ol>
  );
}
